import { TemplateEntryPointEnum } from '__generated__/types';
import { EMessengerChatSourceType } from '@xometry/ui';
import { action, computed, observable } from 'mobx';
import { TemplateModalQueryVariables } from 'utils/graphql/queries/__generated__/templateModal';

interface Source {
  sourceId: string;
  sourceType: EMessengerChatSourceType;
}

type MessageVisibleTo = 'client' | 'partner' | null;

class TemplatesModalStore {
  @observable private messageVisibleTo: MessageVisibleTo = null;

  @observable isOpen = false;

  @observable isBulkActions = false;

  @observable entryPoint: TemplateEntryPointEnum | null = null;

  @observable messageSourceForTemplates: Source | null = null;

  @observable messageSourceForMessage: Source | null = null;

  @observable bulkSourceId: string | undefined = undefined;

  @observable bulkSourceItemIds: string[] | undefined = undefined;

  @computed get messageVisibilityAttributes() {
    return {
      isVisibleToClient: this.messageVisibleTo === 'client',
      isVisibleToPartner: this.messageVisibleTo === 'partner',
    };
  }

  @computed get categoriesRequestParams(): TemplateModalQueryVariables | null {
    if (!this.entryPoint || !this.messageSourceForTemplates || !this.messageSourceForMessage) {
      return null;
    }

    return {
      entryPoint: this.entryPoint,
      templateSourceId: this.messageSourceForTemplates.sourceId,
      templateSourceType: this.messageSourceForTemplates.sourceType,
      messageSourceId: this.messageSourceForMessage.sourceId,
      messageSourceType: this.messageSourceForMessage.sourceType,
    };
  }

  @action openTemplatesModal = ({
    entryPoint,
    messageSourceForTemplates,
    messageSourceForMessage = messageSourceForTemplates,
    isVisibleToClient = false,
    isVisibleToPartner = false,
    isBulkActions = false,
    bulkSourceId,
    bulkSourceItemIds,
  }: {
    entryPoint: TemplateEntryPointEnum;
    messageSourceForTemplates: Source;
    messageSourceForMessage?: Source;
    isVisibleToClient?: boolean;
    isVisibleToPartner?: boolean;
    isBulkActions?: boolean;
    bulkSourceId?: string;
    bulkSourceItemIds?: string[];
  }) => {
    this.entryPoint = entryPoint;
    this.messageSourceForTemplates = messageSourceForTemplates;
    this.messageSourceForMessage = messageSourceForMessage;

    this.isBulkActions = isBulkActions;

    this.bulkSourceId = bulkSourceId;
    this.bulkSourceItemIds = bulkSourceItemIds;

    if (isVisibleToClient) {
      this.messageVisibleTo = 'client';
    } else if (isVisibleToPartner) {
      this.messageVisibleTo = 'partner';
    } else {
      this.messageVisibleTo = null;
    }

    this.isOpen = true;
  };

  @action setMessageVisibleTo = (messageVisibleTo: MessageVisibleTo) => {
    this.messageVisibleTo = messageVisibleTo;
  };

  @action closeTemplatesModal = () => {
    this.isOpen = false;
    this.entryPoint = null;
    this.messageSourceForTemplates = null;
    this.messageSourceForMessage = null;
    this.messageVisibleTo = null;
  };
}

export default TemplatesModalStore;
